import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import type { EmployeeWorkflowAssignment, AssignWorkflowRequest } from '@/types/workflow-assignment';
import * as workflowAssignmentService from '@/services/workflowAssignmentService';
import { useScreenDataMode } from './useScreenDataMode';

// Mock workflow assignment data for demo mode
const getMockWorkflowAssignment = (employeeId: number): EmployeeWorkflowAssignment => {
  return {
    employeeId,
    workflowId: null,
    workflow: null,
    isDefault: true,
    assignedAt: new Date(),
    assignedBy: undefined,
    assignedByName: undefined,
    reason: undefined,
  };
};

export function useWorkflowAssignment(employeeId: number | null) {
  const { mode } = useScreenDataMode('team-members');
  const [assignment, setAssignment] = useState<EmployeeWorkflowAssignment | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAssignment = async () => {
    if (!employeeId) {
      setAssignment(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // In demo mode, return mock data instead of making API calls
      if (mode === 'demo') {
        // Simulate a small delay for realism
        await new Promise(resolve => setTimeout(resolve, 300));
        const mockData = getMockWorkflowAssignment(employeeId);
        setAssignment(mockData);
      } else {
        // In live mode, fetch from API
        const data = await workflowAssignmentService.getEmployeeWorkflowAssignment(employeeId);
        setAssignment(data);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load workflow assignment';
      setError(errorMessage);
      console.error('Error loading workflow assignment:', err);
      
      // Only show toast error in live mode
      if (mode === 'live') {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const assignWorkflow = async (request: AssignWorkflowRequest): Promise<boolean> => {
    if (!employeeId) return false;

    setIsLoading(true);
    setError(null);

    try {
      // In demo mode, simulate the assignment
      if (mode === 'demo') {
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const mockAssignment: EmployeeWorkflowAssignment = {
          employeeId,
          workflowId: request.workflowId,
          workflow: null,
          isDefault: !request.workflowId,
          assignedAt: new Date(),
          assignedBy: 'current-user',
          assignedByName: 'Current User',
          reason: request.reason,
        };
        
        setAssignment(mockAssignment);
        toast.success(request.workflowId ? 'Workflow assigned successfully' : 'Reverted to default workflow');
        return true;
      } else {
        // In live mode, call the API
        const result = await workflowAssignmentService.assignWorkflowToEmployee(employeeId, request);
        
        if (result.success) {
          setAssignment(result.assignment);
          toast.success(result.message);
          return true;
        } else {
          toast.error('Failed to assign workflow');
          return false;
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to assign workflow';
      setError(errorMessage);
      
      // Only show toast error in live mode
      if (mode === 'live') {
        toast.error(errorMessage);
      }
      console.error('Error assigning workflow:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const revertToDefault = async (reason?: string): Promise<boolean> => {
    return assignWorkflow({ workflowId: null, reason });
  };

  useEffect(() => {
    loadAssignment();
  }, [employeeId]);

  return {
    assignment,
    isLoading,
    error,
    assignWorkflow,
    revertToDefault,
    reloadAssignment: loadAssignment,
  };
}
